import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { ActorViewModelSubscriptionTypes, ViewModelUpdateInfo } from '@caslink/common-client';
import { CommonBroadcastService } from '@caslink/common-client/ng-app/common-broadcast/common-broadcast.service';
import { EventTypes } from '@caslink/common-client/src';
import { IDeviceNode } from '@caslink/device-client/ng-app/shared/models/device';
import { DeviceSettingsBaseComponent } from '@caslink/device-client/ng-app/device-settings-base/device-settings-base.component';
import { DeviceInformation } from '@caslink/device-client/ng-app/shared/models/device-information';
import { DeviceModelService } from '@caslink/device-client/ng-app/shared/services/device-model.service';
import { interval, Subject, Subscription } from 'rxjs';
import { throttle } from 'rxjs/operators';
import { SubscriptionTypes } from 'src/app/modules/common/enums/subscription-types.enum';
import { BroadcastService } from 'src/app/modules/common/services/broadcast/broadcast.service';
import { FavoriteEvents } from 'src/app/modules/favorite/enums/favorite-events.enum';
import { FavoriteControl, FavoriteSpace } from 'src/app/modules/favorite/models/favorite';
import { UserEvents } from 'src/app/modules/user/enums/user-events.enum';
import { LocationChange } from 'src/app/modules/user/types/location-change';
import { environment } from '../../../../../environments/environment';
import { NotificationService } from '../../../common/services/notification/notification.service';
import { ToastService } from '../../../common/services/toast/toast.service';
import { ControlSummaryService } from '../../controls/services/control-summary.service';
import { ViewModelSubscriptionService } from './view-model-subscription-service';
import { IViewModelConfirmationSubscription } from '@caslink/device-client/ng-app/shared/models/models';

@Injectable({
    providedIn: 'root'
})
export class DashboardSubscriptionService implements OnDestroy {

    private subscriptions: Subscription[];

    constructor(
        private controlSummaryService: ControlSummaryService,
        private broadcastService: BroadcastService,
        private vmss: ViewModelSubscriptionService,
        private deviceModelService: DeviceModelService,
        private commonBroadcastService: CommonBroadcastService,
        private notificationService: NotificationService,
        private toastService: ToastService,
        private ngZone: NgZone,
    ) {
        this.subscriptions = [
            this.broadcastService.on(FavoriteEvents.FavoriteControl).subscribe((data: FavoriteControl) => vmss.handleControlFavorite(data)),
            this.broadcastService.on(FavoriteEvents.UnfavoriteControl).subscribe((data: FavoriteControl) => vmss.handleControlUnfavorite(data)),
            this.broadcastService.on(FavoriteEvents.FavoriteSpace).subscribe((data: FavoriteSpace) => vmss.handleSpaceFavorite(data)),
            this.broadcastService.on(FavoriteEvents.UnfavoriteSpace).subscribe((data: FavoriteSpace) => vmss.handleSpaceUnfavorite(data)),
            this.broadcastService.on(FavoriteEvents.FavoritePlace).subscribe((data: number) => vmss.handlePlaceFavorite(data)),
            this.broadcastService.on(FavoriteEvents.UnfavoritePlace).subscribe((data: number) => vmss.handlePlaceUnfavorite(data)),
            this.broadcastService.on(UserEvents.ActiveLocationChanged).subscribe((data: LocationChange) => vmss.handleActiveLocationChange(data)),
        ];
    }

    private subscribedToViewModelUpdates = false;

    private readonly viewModelConfirmationSubscriptions = new Map<
        number,
        {
            subscription: IViewModelConfirmationSubscription,
            locationId: number,
            areaId: number,
            deviceName: string
        }>();

    private cacheUpdates = new Map<number, Subject<{url: string, response: Response}>>();
    private readonly controlUpdateTimeout = 1000 * 180;

    public ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    public subscribeToViewModelUpdates(onUpdate: (data: ViewModelUpdateInfo) => void) {
        if (!this.subscribedToViewModelUpdates) {
            this.subscriptions.push(this.commonBroadcastService.on(EventTypes.ViewModelUpdate)
                .subscribe((data: ViewModelUpdateInfo) => {
                    this.ngZone.run(() => {
                        /* Currently, viewModel is boolean for confirmation updates
                        * and a complex object for non-confirmation updates. We should make these separate events, TODO */
                        if (typeof data.viewModel === 'boolean' && data.entityId) {
                            if (this.viewModelConfirmationSubscriptions.has(data.entityId)) {
                                const subscription = this.viewModelConfirmationSubscriptions.get(data.entityId);
                                this.toastService.removeDeviceMessage(data.entityId);
                                this.toastService.showDeviceSuccess(
                                    subscription.locationId,
                                    subscription.areaId,
                                    data.entityId,
                                    `${subscription.deviceName} control updates completed.`,
                                    1000 * 10);

                                this.deviceModelService.stopViewModelConfirmationSubscription(
                                    subscription.locationId,
                                    subscription.areaId,
                                    subscription.subscription
                                );
                                this.viewModelConfirmationSubscriptions.delete(data.entityId);
                            }
                        } else {
                            onUpdate(data);
                        }
                    });
                }));
        }
    }

    public preUpdateControlSummary(
        locationId: number,
        areaId: number,
        deviceId: number,
        deviceName: string
    ) {
        // Attempt to clear initial pending messages
        this.toastService.removeLocationMessage(locationId);
        this.toastService.removeAreaMessage(areaId);

        this.toastService.showDeviceMessage(locationId, areaId, deviceId, `${deviceName} control updates pending...`, this.controlUpdateTimeout, true);
    }

    public updateControlSummary(
        deviceInfo: DeviceInformation,
        originalHeirarchy: IDeviceNode,
        locationId: number,
        areaId: number,
        deviceId: number,
        deviceName: string
    ) {
        this.controlSummaryService
            .updateControlSummary(deviceId, deviceInfo)
            .subscribe(_ => {
                this.startViewModelConfirmationSubscription(
                    originalHeirarchy,
                    deviceInfo.$deviceHierarchy,
                    locationId,
                    areaId,
                    deviceId,
                    deviceName);
            },
            (_: HttpErrorResponse) => {
                this.toastService.removeDeviceMessage(deviceId);
                this.toastService.showDeviceError(locationId, areaId, deviceId, `${deviceName} control updates failed.`, 1000 * 10);
            });
    }

    private setUpCacheSubjectForDevice(id: number) {
        if (!this.cacheUpdates.has(id)) {
            const subject = new Subject<{url: string, response: Response}>();
            // Prevent cache updates more often than every 10 seconds.
            subject.pipe(throttle(val => interval(10000)))
                .subscribe((value: {url: string, response: Response}) => {
                    caches.keys().then(keys => {
                        const dashboardCache = keys.find(key => key.match('^ngsw:.*:device-cache:cache$') !== null);
                        caches.open(dashboardCache).then(cache => {
                            cache.put(value.url, value.response);
                        });
                    });
                });
            this.cacheUpdates.set(id, subject);
        }
    }

    public cacheGetControlsForDeviceResponse(id: number | string, body: any) {
        const url = `${environment.hosts.devices}dashboard/getControlsForDevice/${id}`;
        const response = new Response(JSON.stringify(body), { status: 200, statusText: 'OK' });
        const numId = Number(id);

        this.cacheUpdates.get(numId).next({url, response});
    }

    public startViewModelConfirmationSubscription(
        originalDeviceHeirarchy: IDeviceNode,
        updatedDeviceHeirarchy: IDeviceNode,
        locationId: number,
        areaId: number,
        hardwareItemId: number,
        deviceName: string
    ) {
        const originalViewModel = DeviceSettingsBaseComponent.stripVisitorProperties(originalDeviceHeirarchy);
        const changedViewModel = DeviceSettingsBaseComponent.stripVisitorProperties(updatedDeviceHeirarchy);

        const currentSubscriptionObject = this.viewModelConfirmationSubscriptions.get(hardwareItemId);
        const currentSubscription = currentSubscriptionObject ? currentSubscriptionObject.subscription : undefined;
        const newSubscription = this.deviceModelService.startViewModelConfirmationSubscription(
            locationId,
            areaId,
            currentSubscription, {
                originalViewModel,
                changedViewModel
            });

        // Expire the snackbar message and update request after 90 seconds if it has not gone through.
        setTimeout(() => {
            if (this.viewModelConfirmationSubscriptions.has(hardwareItemId)) {
                this.deviceModelService.stopViewModelConfirmationSubscription(locationId, areaId, newSubscription);
                this.toastService.removeDeviceMessage(hardwareItemId);
                this.toastService.showDeviceWarning(locationId, areaId, hardwareItemId, `${deviceName} control update confirmation timed out.`, 1000 * 10);
            }
        }, this.controlUpdateTimeout);

        const newSubscriptionObject = { subscription: newSubscription, locationId, areaId, deviceName };
        this.viewModelConfirmationSubscriptions.set(hardwareItemId, newSubscriptionObject);
    }

    public setupLiveViewModels(
        locationId: number,
        areaId: number,
        controlSummaryViewModel: any
    ) {
        this.setUpCacheSubjectForDevice(controlSummaryViewModel.identifier.entityId);

        this.notificationService.startUpdates(
            locationId,
            SubscriptionTypes.ViewModel,
            {
                areaId,
                actorViewModelSubscriptionType: ActorViewModelSubscriptionTypes.ViewModelUpdate,
                identifier: controlSummaryViewModel.identifier
            }, {originalViewModel: controlSummaryViewModel});
    }

    public stopLiveViewModels(locationId, areaId, controlSummaryViewModel: any) {
        this.notificationService.stopUpdates(locationId,
        SubscriptionTypes.ViewModel,
        {
            areaId,
            actorViewModelSubscriptionType: ActorViewModelSubscriptionTypes.ViewModelUpdate,
            identifier: controlSummaryViewModel.identifier
        });
    }
}
